import { useMemo } from 'react';
import { useBreakpoint } from 'shared/hooks/use-breakpoint';
import type { DesktopOS, MobileOS } from 'shared/hooks/use-device-os';
import { useDeviceOS } from 'shared/hooks/use-device-os';

export type { MobileOS, DesktopOS };
export type DeviceType = 'mobile' | 'tablet' | 'desktop';

export type UsePlatformResult = {
  // Device class
  deviceType: DeviceType;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;

  // Orientation
  isPortrait: boolean;
  isLandscape: boolean;

  // OS detection
  mobileOS: MobileOS; // android/ios on handhelds, otherwise null
  desktopOS: DesktopOS; // mac/windows/linux on desktops, otherwise null
  isAndroid: boolean;
  isIOS: boolean;
  isMac: boolean;
  isWindows: boolean;
  isLinux: boolean;
};

/**
 * usePlatform
 * Unified platform and device hook.
 * - deviceType: mobile/tablet/desktop (UA/device heuristics, not viewport width)
 * - orientation: isPortrait/isLandscape (from useBreakpoint)
 * - OS detection: android/ios (mobile), mac/windows/linux (desktop) via userAgent sniffing
 *
 * SSR-safe: returns conservative defaults when window/navigator are not available.
 */
export const usePlatform = (): UsePlatformResult => {
  const { isPortrait, isLandscape, width, height } = useBreakpoint();
  const { mobileOS, desktopOS, isAndroid, isIOS, isMac, isWindows, isLinux } = useDeviceOS();

  // Track width for device classification:
  // - handhelds (android/ios): use the smaller viewport side to be orientation-invariant
  // - desktops: use actual viewport width
  const stableWidth = useMemo(() => {
    if (width == null || height == null) return null;
    // Orientation-invariant for handhelds only (mobileOS is set, but desktopOS is NOT)
    const useMin = mobileOS !== null && desktopOS === null;
    return useMin ? Math.min(width, height) : width;
  }, [width, height, mobileOS, desktopOS]);

  // Determine deviceType strictly by width ranges
  const deviceType: DeviceType = useMemo(() => {
    const w = stableWidth;
    if (w == null) return 'desktop'; // SSR-safe default
    if (w >= 900) return 'desktop';
    if (w >= 834) return 'tablet';
    return 'mobile';
  }, [stableWidth]);

  return {
    deviceType,
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet',
    isDesktop: deviceType === 'desktop',

    isPortrait,
    isLandscape,

    mobileOS,
    desktopOS,
    isAndroid,
    isIOS,
    isMac,
    isWindows,
    isLinux,
  };
};

export default usePlatform;
